import React, { useState, useMemo } from 'react';
import { Flame, Route, Eye, RotateCcw } from 'lucide-react';
import { C } from '../design-system/primitives/constants';
import { CAROLINA_BLUE } from '../../config/uncConstants';
import { interpolateColorStops } from '../../utils/colorInterpolation';

// Cold-to-hot color stops for baseball field heatmap
const HEAT_STOPS = [
  { pos: 0.0, r: 59, g: 130, b: 246 },   // Blue — cold
  { pos: 0.25, r: 6, g: 182, b: 212 },    // Cyan
  { pos: 0.5, r: 34, g: 197, b: 94 },     // Green
  { pos: 0.75, r: 234, g: 179, b: 8 },    // Gold
  { pos: 1.0, r: 239, g: 68, b: 68 },     // Red — hot
];

// Baseball diamond zones — SVG paths in a 500x500 viewBox
// Diamond oriented: home plate at bottom-center, outfield at top
const FIELD_ZONES = {
  'Home Plate': {
    path: 'M 250,440 L 220,410 L 250,380 L 280,410 Z',
    cx: 250, cy: 410, shortName: 'HP',
  },
  'Pitcher\'s Mound': {
    path: 'M 235,320 A 15,15 0 1,1 265,320 A 15,15 0 1,1 235,320 Z',
    cx: 250, cy: 320, shortName: 'P',
  },
  '1st Base': {
    path: 'M 330,340 L 370,300 L 410,340 L 370,380 Z',
    cx: 370, cy: 340, shortName: '1B',
  },
  '2nd Base': {
    path: 'M 230,240 L 250,220 L 270,240 L 250,260 Z',
    cx: 250, cy: 240, shortName: '2B',
  },
  '3rd Base': {
    path: 'M 90,340 L 130,300 L 170,340 L 130,380 Z',
    cx: 130, cy: 340, shortName: '3B',
  },
  'SS Area': {
    path: 'M 150,260 L 200,220 L 230,260 L 180,300 Z',
    cx: 190, cy: 260, shortName: 'SS',
  },
  '2B Area': {
    path: 'M 270,260 L 300,220 L 350,260 L 320,300 Z',
    cx: 310, cy: 260, shortName: '2B-R',
  },
  'LF': {
    path: 'M 20,200 L 60,100 L 150,60 L 150,220 L 90,300 Z',
    cx: 90, cy: 160, shortName: 'LF',
  },
  'CF': {
    path: 'M 150,60 L 250,30 L 350,60 L 350,180 L 250,200 L 150,180 Z',
    cx: 250, cy: 110, shortName: 'CF',
  },
  'RF': {
    path: 'M 350,60 L 440,100 L 480,200 L 410,300 L 350,220 Z',
    cx: 410, cy: 160, shortName: 'RF',
  },
};

// Infield diamond outline and foul lines
const DIAMOND_LINES = [
  // Foul lines
  'M 250,440 L 20,200',
  'M 250,440 L 480,200',
  // Basepaths
  'M 250,440 L 370,340',
  'M 370,340 L 250,240',
  'M 250,240 L 130,340',
  'M 130,340 L 250,440',
  // Outfield arc
  'M 20,200 Q 250,0 480,200',
];

// Path line color palette for multiple player tracks
const PATH_COLORS = [
  CAROLINA_BLUE,
  CAROLINA_BLUE,
  '#FF6B35',
  '#4ECDC4',
  '#7B68EE',
  '#FF4444',
  '#00CC66',
  '#E91E90',
];

function ZoneTooltip({ zone, value, max }) {
  if (!zone) return null;
  const pct = max > 0 ? ((value / max) * 100).toFixed(0) : 0;
  return (
    <div
      className="absolute z-10 px-2.5 py-1.5 rounded-lg text-[10px] pointer-events-none"
      style={{
        background: 'rgba(0,0,0,0.9)',
        border: `1px solid ${C.border}`,
        color: C.text,
        transform: 'translate(-50%, -100%)',
        top: -8,
        left: '50%',
      }}
    >
      <div className="font-semibold">{zone}</div>
      <div style={{ color: C.t2 }}>{value} events ({pct}% of max)</div>
    </div>
  );
}

export default function PracticeFilmOverlay({
  heatmapData = {},
  pathLines = [],
  title = 'Practice Film Analysis',
  showHeatmap = true,
  showPaths = true,
  onZoneClick,
}) {
  const [hoveredZone, setHoveredZone] = useState(null);
  const [layerToggles, setLayerToggles] = useState({
    heatmap: showHeatmap,
    paths: showPaths,
  });

  // Compute max value for normalization
  const maxValue = useMemo(() => {
    let max = 0;
    for (const val of Object.values(heatmapData)) {
      if (val > max) max = val;
    }
    return max || 1;
  }, [heatmapData]);

  const getZoneColor = (zoneName) => {
    const val = heatmapData[zoneName] || 0;
    const normalized = val / maxValue;
    const { r, g, b } = interpolateColorStops(HEAT_STOPS, normalized);
    const alpha = 0.15 + normalized * 0.55;
    return `rgba(${r},${g},${b},${alpha})`;
  };

  const toggleLayer = (layer) => {
    setLayerToggles((prev) => ({ ...prev, [layer]: !prev[layer] }));
  };

  return (
    <div className="space-y-3">
      {/* Header + controls */}
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-xs font-semibold" style={{ color: C.text }}>{title}</h4>
          <p className="text-[10px] mt-0.5" style={{ color: C.t3 }}>
            {Object.keys(heatmapData).length} zones · {pathLines.length} path{pathLines.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => toggleLayer('heatmap')}
            className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-all"
            style={{
              background: layerToggles.heatmap ? CAROLINA_BLUE + '22' : C.s2,
              color: layerToggles.heatmap ? CAROLINA_BLUE : C.t3,
              border: `1px solid ${layerToggles.heatmap ? CAROLINA_BLUE + '55' : C.border}`,
            }}
          >
            <Flame size={10} /> Heatmap
          </button>
          <button
            onClick={() => toggleLayer('paths')}
            className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium transition-all"
            style={{
              background: layerToggles.paths ? CAROLINA_BLUE + '22' : C.s2,
              color: layerToggles.paths ? CAROLINA_BLUE : C.t3,
              border: `1px solid ${layerToggles.paths ? CAROLINA_BLUE + '55' : C.border}`,
            }}
          >
            <Route size={10} /> Paths
          </button>
        </div>
      </div>

      {/* SVG Field */}
      <div
        className="rounded-xl overflow-hidden relative"
        style={{ background: C.s1, border: `1px solid ${C.border}` }}
      >
        <svg
          viewBox="0 0 500 470"
          className="w-full"
          style={{ maxWidth: 600, margin: '0 auto', display: 'block' }}
        >
          {/* Dark background */}
          <rect x="0" y="0" width="500" height="470" fill={C.bg || '#000'} rx="8" />

          {/* Field outline */}
          {DIAMOND_LINES.map((d, i) => (
            <path
              key={i}
              d={d}
              fill="none"
              stroke="rgba(255,255,255,0.08)"
              strokeWidth="1"
            />
          ))}

          {/* Heatmap zones */}
          {layerToggles.heatmap && Object.entries(FIELD_ZONES).map(([name, zone]) => {
            const isHovered = hoveredZone === name;
            const value = heatmapData[name] || 0;
            return (
              <g key={name}>
                <path
                  d={zone.path}
                  fill={value > 0 ? getZoneColor(name) : 'rgba(255,255,255,0.03)'}
                  stroke={isHovered ? CAROLINA_BLUE : 'rgba(255,255,255,0.06)'}
                  strokeWidth={isHovered ? 2 : 0.5}
                  style={{ cursor: 'pointer', transition: 'fill 0.2s, stroke 0.2s' }}
                  onMouseEnter={() => setHoveredZone(name)}
                  onMouseLeave={() => setHoveredZone(null)}
                  onClick={() => onZoneClick?.(name, value)}
                />
                {/* Zone label */}
                {value > 0 && (
                  <text
                    x={zone.cx}
                    y={zone.cy}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill="rgba(255,255,255,0.9)"
                    fontSize="10"
                    fontWeight="600"
                    fontFamily="monospace"
                    style={{ pointerEvents: 'none' }}
                  >
                    {value}
                  </text>
                )}
                {/* Short name label */}
                <text
                  x={zone.cx}
                  y={zone.cy + (value > 0 ? 12 : 0)}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fill="rgba(255,255,255,0.35)"
                  fontSize="8"
                  fontWeight="500"
                  style={{ pointerEvents: 'none' }}
                >
                  {zone.shortName}
                </text>
              </g>
            );
          })}

          {/* Path lines */}
          {layerToggles.paths && pathLines.map((path, i) => {
            if (!path.points || path.points.length < 2) return null;
            const color = PATH_COLORS[i % PATH_COLORS.length];
            const d = path.points
              .map((p, j) => `${j === 0 ? 'M' : 'L'} ${p.x},${p.y}`)
              .join(' ');
            return (
              <g key={`path-${i}`}>
                {/* Path shadow */}
                <path
                  d={d}
                  fill="none"
                  stroke="rgba(0,0,0,0.5)"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {/* Main path */}
                <path
                  d={d}
                  fill="none"
                  stroke={color}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.85"
                />
                {/* Start marker */}
                <circle
                  cx={path.points[0].x}
                  cy={path.points[0].y}
                  r="4"
                  fill={color}
                  stroke="#000"
                  strokeWidth="1"
                />
                {/* End marker (arrow-like) */}
                <circle
                  cx={path.points[path.points.length - 1].x}
                  cy={path.points[path.points.length - 1].y}
                  r="3"
                  fill="#000"
                  stroke={color}
                  strokeWidth="2"
                />
                {/* Player label */}
                {path.label && (
                  <text
                    x={path.points[0].x}
                    y={path.points[0].y - 8}
                    textAnchor="middle"
                    fill={color}
                    fontSize="8"
                    fontWeight="600"
                  >
                    {path.label}
                  </text>
                )}
              </g>
            );
          })}

          {/* Base markers */}
          <circle cx="250" cy="440" r="5" fill="white" opacity="0.5" /> {/* Home */}
          <rect x="366" y="336" width="8" height="8" fill="white" opacity="0.5" transform="rotate(45,370,340)" /> {/* 1B */}
          <rect x="246" y="236" width="8" height="8" fill="white" opacity="0.5" transform="rotate(45,250,240)" /> {/* 2B */}
          <rect x="126" y="336" width="8" height="8" fill="white" opacity="0.5" transform="rotate(45,130,340)" /> {/* 3B */}
          <circle cx="250" cy="320" r="3" fill="white" opacity="0.3" /> {/* Mound */}
        </svg>

        {/* Hover tooltip */}
        {hoveredZone && (
          <div
            className="absolute top-3 left-3 px-2.5 py-1.5 rounded-lg"
            style={{
              background: 'rgba(0,0,0,0.9)',
              border: `1px solid ${C.border}`,
            }}
          >
            <div className="text-[10px] font-semibold" style={{ color: C.text }}>{hoveredZone}</div>
            <div className="text-[9px]" style={{ color: C.t2 }}>
              {heatmapData[hoveredZone] || 0} events
              {maxValue > 0 && ` · ${((heatmapData[hoveredZone] || 0) / maxValue * 100).toFixed(0)}% intensity`}
            </div>
          </div>
        )}
      </div>

      {/* Path legend */}
      {layerToggles.paths && pathLines.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {pathLines.map((path, i) => (
            <div
              key={i}
              className="flex items-center gap-1.5 px-2 py-1 rounded-md"
              style={{ background: C.s2, border: `1px solid ${C.border}` }}
            >
              <div
                className="w-2 h-2 rounded-full"
                style={{ background: PATH_COLORS[i % PATH_COLORS.length] }}
              />
              <span className="text-[9px] font-medium" style={{ color: C.t2 }}>
                {path.label || `Player ${i + 1}`}
              </span>
              <span className="text-[9px]" style={{ color: C.t3 }}>
                {path.points?.length || 0} pts
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Color scale legend */}
      {layerToggles.heatmap && Object.keys(heatmapData).length > 0 && (
        <div className="flex items-center gap-2">
          <span className="text-[9px]" style={{ color: C.t3 }}>Low</span>
          <div
            className="flex-1 h-1.5 rounded-full"
            style={{
              background: 'linear-gradient(to right, rgba(59,130,246,0.6), rgba(6,182,212,0.6), rgba(34,197,94,0.6), rgba(234,179,8,0.6), rgba(239,68,68,0.6))',
            }}
          />
          <span className="text-[9px]" style={{ color: C.t3 }}>High</span>
        </div>
      )}
    </div>
  );
}
